import { useCallback, useEffect, useRef, useState, type RefObject, type SyntheticEvent } from 'react'

import type { TileVideoEntry } from '../utils/tileVideoQueue'

/** С какой секунды текущего ролика подгружаем следующий (не раньше половины короткого лупа): при старте страницы
 *  очередь не тянет лишнего — следующий файл идёт только у плитки, которая уже играет. */
const WARM_AFTER_SECONDS = 1

/**
 * Ход очереди роликов у одной плитки (utils/tileVideoQueue): какой ролик сейчас, когда переключать и что греть.
 * Владелец 23.10: «чтобы не грузились все видео сразу, особенно при запуске страницы» — поэтому:
 *   • у плитки по-прежнему ОДИН играющий <video>; очередь лишь меняет ему src по концу ролика и продолжает играть;
 *   • следующий ролик греется невидимым <video preload=auto> (TileVideoWarm) только пока плитка играет и текущий ролик
 *     прошёл WARM_AFTER_SECONDS; остановилась или выгружена (ушла за экран) — прогрев снимается вместе с загрузкой;
 *   • без очереди (обычная карточка, одна версия с роликом) ничего из этого нет: loop как раньше.
 */
export function useTileVideoQueue(ref: RefObject<HTMLVideoElement | null>, queue: TileVideoEntry[]) {
	const [idx, setIdx] = useState(0)
	const [warm, setWarm] = useState(false)
	const n = queue.length
	const queued = n > 1
	const i = idx < n ? idx : 0
	const entry = queue[i] ?? null
	const next = queued ? queue[(i + 1) % n] : null
	// Смена src по концу ролика: элемент после неё сам не стартует — запускаем в эффекте, когда атрибут уже обновлён.
	const swapped = useRef(false)
	const onEnded = useCallback(() => {
		if (!queued) return
		swapped.current = true
		setWarm(false)
		setIdx((cur) => (cur + 1) % n)
	}, [queued, n])
	useEffect(() => {
		if (!swapped.current) return
		swapped.current = false
		void ref.current?.play().catch(() => {})
	}, [i, ref])
	const onTimeUpdate = useCallback(
		(e: SyntheticEvent<HTMLVideoElement>) => {
			if (!queued || warm) return
			const v = e.currentTarget
			const half = Number.isFinite(v.duration) && v.duration > 0 ? v.duration / 2 : WARM_AFTER_SECONDS
			if (v.currentTime >= Math.min(WARM_AFTER_SECONDS, half)) setWarm(true)
		},
		[queued, warm],
	)
	/** Ролик остановлен или выгружен (пауза, снятие src) — следующий не держим. */
	const onStop = useCallback(() => setWarm(false), [])
	return {
		/** Текущий ролик очереди (null — роликов нет). */
		entry,
		/** Очередь есть (больше одного ролика): loop снят, работают onEnded/onTimeUpdate/onStop. */
		queued,
		/** Первый ли это ролик очереди: его постер — постер плитки, у остальных свой. */
		first: i === 0,
		/** Чей ролик играет — для подсветки кадра версии; без очереди null. */
		playingId: queued ? (entry?.id ?? null) : null,
		/** Что греть сейчас (TileVideoWarm); null — ничего. */
		warmEntry: queued && warm && next ? next : null,
		onEnded,
		onTimeUpdate,
		onStop,
	}
}
